/**
 * Case Configuration Mixin
 * 
 * Provides easy access to per-case configuration throughout the application.
 * Configuration is stored in the Vuex store under state.case.config
 * 
 * CONFIGURATION OPTIONS:
 * ----------------------
 * 
 * logo_url (string):
 *   Custom logo URL for the case. 
 *   Default: "/images/GLOBAL/LOGO/BYOCS-logo.png"
 * 
 * allowed_platforms (array|null):
 *   List of allowed platform strings (e.g., ["mli", "healio", "lms"]).
 *   If null or not set, all platforms are allowed.
 *   Default: null (allow all)
 * 
 * credit_message (object):
 *   Per-platform credit completion messages. Supports HTML.
 *   Format: { "platform_name": "HTML message", "default": "Default HTML message" }
 *   Default: "You have completed this lesson.<br><br><b>You may continue with course requirements for credit.</b>"
 * 
 * unlock_library_credit (object):
 *   Controls when Library and Claim Credit buttons become active.
 *   Properties:
 *     - unlock_at_substep_id (number): Unlock when reaching this substep ID
 *     - unlock_at_step (number): Unlock when currentStep >= this value
 *     - always_unlocked_steps (array): Steps where buttons are always unlocked
 *   Default: Unlocks at last substep of step 1 and all subsequent steps
 * 
 * skip_intro_video_after_step_1 (boolean):
 *   If true, skips the intro video on step 2 and later.
 *   Default: true
 * 
 * disable_video_autoplay (boolean):
 *   If true, completely hides the video autoplay toggle from the UI.
 *   Default: false (show the toggle)
 * 
 * video_autoplay_default (boolean):
 *   Sets the default state for video autoplay when the case loads.
 *   If not set, uses the existing default (false/off).
 *   Default: false
 * 
 * hide_previous_avatars (boolean):
 *   If true, hides avatars that were selected in previous steps.
 *   Default: false
 * 
 * EXAMPLE CONFIG JSON:
 * --------------------
 * {
 *   "logo_url": "/images/CUSTOM/logo.png",
 *   "allowed_platforms": ["mli", "healio", "lms", "nexus", "mycme"],
 *   "credit_message": {
 *     "default": "You have completed this lesson.<br><br><b>You may continue with course requirements for credit.</b>",
 *     "mli": "Thank you for completing this MLI course!<br><br><b>Please proceed to claim your credit.</b>"
 *   },
 *   "unlock_library_credit": {
 *     "unlock_at_substep_id": 11601,
 *     "unlock_at_step": 2,
 *     "always_unlocked_steps": [5, 6]
 *   },
 *   "skip_intro_video_after_step_1": true,
 *   "hide_previous_avatars": true,
 *   "disable_video_autoplay": false,
 *   "video_autoplay_default": true
 * }
 */

import { mapState } from 'vuex';

export default {
  computed: {
    ...mapState({
      caseConfig: (state) => state.case.config,
      currentPlatform: (state) => state.user.platform,
    }),

    /**
     * Get the logo URL for the current case
     * @returns {string} Logo URL
     */
    configLogoUrl() {
      return this.caseConfig?.logo_url || '/images/GLOBAL/LOGO/BYOCS-logo.png';
    },

    /**
     * Get allowed platforms for the current case
     * @returns {array|null} Array of allowed platforms, or null if all are allowed
     */
    configAllowedPlatforms() {
      return this.caseConfig?.allowed_platforms || null;
    },

    /**
     * Check if the current platform is allowed
     * @returns {boolean} True if platform is allowed
     */
    isPlatformAllowed() {
      const allowed = this.configAllowedPlatforms;
      if (!allowed || allowed.length === 0) {
        return true; // No config = allow all
      }
      return allowed.includes(this.currentPlatform);
    },

    /**
     * Get the credit message for the current platform
     * @returns {string} HTML credit message
     */
    configCreditMessage() {
      const defaultMessage = 'You have completed this lesson.<br><br><b>You may continue with course requirements for credit.</b>';
      const messages = this.caseConfig?.credit_message;
      
      if (!messages) {
        return defaultMessage;
      }
      
      // Check for platform-specific message first
      if (messages[this.currentPlatform]) {
        return messages[this.currentPlatform];
      }
      
      // Fall back to default message in config, then hardcoded default
      return messages.default || defaultMessage;
    },

    /**
     * Get the credit button config for the current platform
     * @returns {object|null} Button config with text, url, and optional color
     */
    configCreditButton() {
      const buttons = this.caseConfig?.credit_button;
      
      if (!buttons) {
        return null;
      }
      
      // Check for platform-specific button first
      if (buttons[this.currentPlatform]) {
        return buttons[this.currentPlatform];
      }
      
      // Fall back to default button in config
      return buttons.default || null;
    },

    /**
     * Get unlock configuration for library/credit
     * @returns {object} Unlock configuration
     */
    configUnlockLibraryCredit() {
      return this.caseConfig?.unlock_library_credit || null;
    },

    /**
     * Check if intro video should be skipped after step 1
     * @returns {boolean} True if should skip
     */
    configSkipIntroVideoAfterStep1() {
      // Default to true (skip) if not configured
      if (this.caseConfig?.skip_intro_video_after_step_1 === undefined) {
        return true;
      }
      return this.caseConfig.skip_intro_video_after_step_1;
    },

    /**
     * Check if previous avatars should be hidden
     * @returns {boolean} True if should hide
     */
    configHidePreviousAvatars() {
      return this.caseConfig?.hide_previous_avatars || false;
    },

    /**
     * Check if video autoplay toggle should be disabled/hidden
     * @returns {boolean} True if autoplay toggle should be hidden
     */
    configDisableVideoAutoplay() {
      return this.caseConfig?.disable_video_autoplay || false;
    },

    /**
     * Get the default value for video autoplay
     * @returns {boolean|null} Default autoplay state, or null to use existing default
     */
    configVideoAutoplayDefault() {
      if (this.caseConfig?.video_autoplay_default === undefined) {
        return null; // Use existing default behavior
      }
      return this.caseConfig.video_autoplay_default;
    },
  },

  methods: {
    /**
     * Check if library/credit should be unlocked based on current state
     * @param {number} currentStep - Current step number
     * @param {object} subStep - Current substep object
     * @param {array} steps - All steps array
     * @returns {boolean} True if should be unlocked
     */
    isLibraryCreditUnlocked(currentStep, subStep, steps) {
      const config = this.configUnlockLibraryCredit;
      
      // If no config, use default behavior:
      // Unlock at last substep of step 1 and all subsequent steps
      if (!config) {
        if (currentStep > 1) {
          return true;
        }
        // Check if we're at the last substep of step 1
        const step1 = steps.find(s => s.id === 1);
        if (step1 && step1.sub_steps && step1.sub_steps.length > 0) {
          const lastSubstep = step1.sub_steps[step1.sub_steps.length - 1];
          return subStep.id === lastSubstep.id;
        }
        return false;
      }

      // Check always_unlocked_steps first
      if (config.always_unlocked_steps && config.always_unlocked_steps.includes(currentStep)) {
        return true;
      }

      // Check unlock_at_step: unlock for steps AFTER the specified step
      // (e.g., unlock_at_step: 1 means unlock starting from step 2)
      if (config.unlock_at_step && currentStep > config.unlock_at_step) {
        return true;
      }

      // Check unlock_at_substep_id: unlock when reaching this specific substep
      if (config.unlock_at_substep_id && subStep.id >= config.unlock_at_substep_id) {
        return true;
      }

      return false;
    },

    /**
     * Get dynamic patient count text based on number of steps
     * @param {array} steps - All steps array
     * @returns {string} Text like "Treat up to 3 patients"
     */
    getPatientCountText(steps) {
      // Count steps that are patient/avatar selection steps (typically all except intro/credit)
      // We'll use the total number of steps as a proxy for patient count
      const patientSteps = steps ? steps.length : 3;
      return `Treat up to ${patientSteps} patients`;
    },
  },
};
